import React, { useState, useEffect, useRef } from 'react';
import { Menu } from 'lucide-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useOrgAuth } from '../../contexts/OrgAuthContext';
import UserMenu from '../UserMenu';
import LanguageSwitcher from '../LanguageSwitcher';
import NotificationsBell from '../notifications/NotificationsBell';
import NavLauncher from '../NavLauncher';
import { SidebarContext } from './SidebarContext';
import { Z } from '../../lib/zIndex';
import { useDialogBackHandler } from '../../hooks/useDialogBackHandler';
import { useVisualViewportHeight } from '../../hooks/useVisualViewportHeight';

export { SidebarContext } from './SidebarContext';

interface AppLayoutProps {
  children: React.ReactNode;
}

/**
 * Routes that should start with the module sidebar collapsed so their content
 * gets the full viewport width: the procurement documents explorer and the
 * large in-browser tools (document-comparison, archive-manager, pdf-editor).
 * Detected from the raw pathname so the rule applies to every entry point —
 * in-app navigation, new tabs, direct visits, refreshes.
 */
export function isFullBleedPath(pathname: string): boolean {
  const seg = pathname.split('/');
  // /app/procurement/explore/:refConsultationId/documents
  if (
    seg.length === 6 &&
    seg[1] === 'app' &&
    seg[2] === 'procurement' &&
    seg[3] === 'explore' &&
    seg[4] !== '' &&
    seg[5] === 'documents'
  ) {
    return true;
  }
  return (
    pathname === '/app/tools/document-comparison' ||
    pathname === '/app/tools/archive-manager' ||
    pathname === '/app/tools/pdf-editor'
  );
}

export function AppLayout({ children }: AppLayoutProps) {
  const { session } = useOrgAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Full-bleed content routes — the procurement documents explorer and the
  // large tools — render best with the module sidebar collapsed for maximum
  // width, so they default to closed (still toggleable via the sidebar rail).
  const isFullBleed = isFullBleedPath(location.pathname);

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // ?menu=closed|open forces the module sidebar state app-wide — e.g. arriving
    // at explore via "Filtrage libre" wants the filter panel open and the sidebar
    // shut, which ?panel=open alone can't express.
    const menu = searchParams.get('menu');
    if (menu === 'closed') return false;
    if (menu === 'open') return true;
    if (isFullBleed) return false;
    return window.innerWidth >= 768;
  });
  const [hasSidebar, setHasSidebar] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.innerWidth < 768);
  // iOS: pin the shell to the visual viewport while the keyboard is open so
  // bottom composers (AI chat…) stay visible — Android handles it via the
  // `interactive-widget=resizes-content` viewport meta; the hook is inert
  // without a keyboard.
  const shellRef = useRef<HTMLDivElement>(null);
  useVisualViewportHeight(shellRef, true);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useDialogBackHandler(isMobileViewport && sidebarOpen, () => setSidebarOpen(false));

  // Close sidebar on mobile whenever the route changes
  useEffect(() => {
    if (isMobileViewport) setSidebarOpen(false);
  }, [location.pathname, isMobileViewport]);

  // ?menu=closed|open overrides the sidebar state whenever the param changes.
  const menuParam = searchParams.get('menu');
  useEffect(() => {
    if (menuParam === 'closed') setSidebarOpen(false);
    else if (menuParam === 'open') setSidebarOpen(true);
  }, [menuParam]);

  // Full-bleed routes default to a collapsed sidebar when no ?menu= override is
  // present. Fires only on arrival (the match flips), so a manual toggle after
  // arriving is respected.
  useEffect(() => {
    if (menuParam) return;
    if (isFullBleed) setSidebarOpen(false);
  }, [isFullBleed, menuParam]);

  return (
    <div ref={shellRef} className="flex flex-col h-[100dvh] overflow-hidden bg-background text-slate-900 font-sans">
      <header className={`flex-shrink-0 box-content h-14 bg-white border-b border-slate-200 flex items-center px-4 z-[${Z.header}] pt-safe`}>
        {hasSidebar && (
          <button className="mr-3 md:hidden p-1 text-slate-500 hover:text-slate-700" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <Menu size={20} />
          </button>
        )}

        {/* Org/App Name */}
        <NavLauncher />

        <div className="w-px h-6 bg-slate-200 mx-4 hidden md:block" />

        <div className="flex-1" id="global-header-center" />

        {/* Right side controls */}
        <div className="flex items-center gap-1 sm:gap-4 shrink-0">
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>

          <div className="flex items-center gap-2" id="global-header-actions" />

          <NotificationsBell />

          <div className="pl-2 flex items-center">
            {session ? <UserMenu /> : null}
          </div>
        </div>
      </header>
      <div className="flex flex-1 overflow-hidden relative">
        <SidebarContext.Provider value={{ isOpen: sidebarOpen, toggle: () => setSidebarOpen(!sidebarOpen), hasSidebar, setHasSidebar }}>
          {children}
        </SidebarContext.Provider>
      </div>
    </div>
  );
}
